import React, { useMemo, useState } from "react";
import DetailedView from "@/components/layouts/DetailedView";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useDropdownClosing } from "@/services/hooks/useDropdownClosing";
import { useLeads, useLeadTypes } from "../../hooks";
import { ComputedFields } from "@/services/api/types";
import LeadTopFields from "../LeadTopFields";
import { TopFieldsContext } from "@/components/layouts/TopFields";
import Ownership from "@/components/layouts/Ownership";
import LeadDetailFields from "../LeadDetailFields/LeadDetailFields";
import { Button } from "@/components/ui/button";
import { LeadStatus } from "../../types";
import { useLeadActions } from "@/services/hooks/entities/lead/useLeadActions";
import { useLicenseServiceItems } from "@/entities/settings/hooks";
import LeadKPIs from "@/pages/leads/components/LeadKPIs";
import NextSteps from "@/components/layouts/NextSteps/NextSteps";
import { useIsDemoLead } from "@/assets/mockupData";
import LeadConvertedBanner from "./LeadConvertedBanner";
import LeadDealSuggestionBanner from "./LeadDealSuggestionBanner";
import { useConvertDeepLink } from "./useConvertDeepLink";
import { EntityData } from "@/services/api/types";
import { buildClosedRecordState } from "@/components/layouts/Activities/components/ActivityClosedRecordEmptyState";
import FromDreamerForDemo from "@/components/layouts/FromDreamer/FromDreamerForDemo";
import OptionsDropdownMenu from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { ChevronDown } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import { cn } from "@/utils/utils";

const LeadDetailedView: React.FC = () => {
  const { t } = useTranslation();
  const { leadId } = useParams<{ leadId: string }>();
  const [view, setView] = useState<"deals" | "companies" | "people">("deals");
  const [isCloseLeadDropdownOpen, setIsCloseLeadDropdownOpen] = useState(false);

  const { isDropdownClosing } = useDropdownClosing();

  const {
    entities: [leadEntity],
    isError: isLeadError
  } = useLeads([leadId || ""], {
    forceRefetch: true,
    computed_fields: ComputedFields.ALL
  });

  const sources = useLeadTypes().data?.leadsTypes ?? [];
  const { licenseItems: licenseTypes } = useLicenseServiceItems();

  const {
    lead,
    linkedDeal,
    isTasksDrawerOpen,
    toggleTasksDrawer,
    handleMarkLeadAsICP,
    handleOpenConvertLeadModal,
    handleOpenDisqualifyLeadModal,
    handleUnmarkAsStuck,
    onUpdateField,
    getTopOptions,
    getOptions,
    renderQualifyMessage,
    renderModals,
    showQualifyAndConvert
  } = useLeadActions(leadEntity, false);

  const isDemo = useIsDemoLead(lead?.id || "");
  const navigate = useNavigate();

  useConvertDeepLink(lead?.status, handleOpenConvertLeadModal);

  // Once a lead reaches a terminal state its activity moves to a linked record;
  // the Activities tab shows a closed-record empty state pointing there.
  const leadStatus = lead?.status;
  const leadContactId = lead?.contactId;
  const linkedDealId = linkedDeal?.id;
  const closedRecordState = useMemo(() => {
    switch (leadStatus) {
      case LeadStatus.CONVERTED:
        return linkedDealId
          ? buildClosedRecordState({
              sourceEntityType: EntityType.LEAD,
              targetEntityType: EntityType.DEAL,
              targetId: linkedDealId,
              action: t("activities.closedRecord.convertedTo", {
                target: t("entities.names.deal")
              }),
              navigate,
              t
            })
          : undefined;
      case LeadStatus.DISQUALIFIED:
        return leadContactId
          ? buildClosedRecordState({
              sourceEntityType: EntityType.LEAD,
              targetEntityType: EntityType.PERSON,
              targetId: leadContactId,
              action: t("activities.closedRecord.disqualified"),
              navigate,
              t
            })
          : undefined;
      case LeadStatus.CLOSED_QUALIFIED:
        return leadContactId
          ? buildClosedRecordState({
              sourceEntityType: EntityType.LEAD,
              targetEntityType: EntityType.PERSON,
              targetId: leadContactId,
              action: t("activities.closedRecord.closedQualified"),
              navigate,
              t
            })
          : undefined;
      default:
        return undefined;
    }
  }, [leadStatus, leadContactId, linkedDealId, navigate, t]);

  return (
    <>
      <DetailedView
        entityData={lead as EntityData}
        entityType={EntityType.LEAD}
        isError={isLeadError}
        options={getOptions()}
        topOptions={getTopOptions()}
        leadStatus={lead?.status}
        onUpdateField={onUpdateField}
        isTasksDrawerOpen={isTasksDrawerOpen}
        toggleTasksDrawer={toggleTasksDrawer}
        closedRecordState={closedRecordState}
        topRightComponent={
          showQualifyAndConvert() ? (
            <div data-test="lead-detailed-view-actions" className="flex gap-2">
              <OptionsDropdownMenu
                options={{
                  closeLeadAsQualified: () =>
                    handleOpenDisqualifyLeadModal("closedAsQualified"),
                  disqualifyLead: () =>
                    handleOpenDisqualifyLeadModal("disqualified")
                }}
                tPrefix="detailedView.options"
                onOpenChange={setIsCloseLeadDropdownOpen}
                triggerDataTest="lead-detailed-view-close-lead-button"
                customTrigger={
                  <Button
                    variant="secondary"
                    className="flex gap-2"
                  >
                    {t("leads.leadView.closeLead")}
                    <IconContainer
                      size="5"
                      color="current"
                      className={cn(
                        "transition-transform duration-200",
                        isCloseLeadDropdownOpen ? "rotate-180" : ""
                      )}
                    >
                      <ChevronDown />
                    </IconContainer>
                  </Button>
                }
              />
              <Button
                data-test="lead-detailed-view-convert-button"
                variant="secondary"
                onClick={
                  lead?.status === LeadStatus.CONFIRM_ICP
                    ? handleMarkLeadAsICP
                    : handleOpenConvertLeadModal
                }
              >
                {t(
                  `leads.leadView.${lead?.status === LeadStatus.CONFIRM_ICP ? "markAsIcp" : "convertLead"}`
                )}
              </Button>
            </div>
          ) : undefined
        }
        topFields={
          <LeadTopFields
            data-test="lead-detailed-view-top-fields"
            lead={lead}
            onUpdateField={onUpdateField}
            handleUnmarkAsStuck={handleUnmarkAsStuck}
            handleOpenDisqualifyLeadModal={handleOpenDisqualifyLeadModal}
            licenseTypes={licenseTypes}
            context={
              {
                setView,
                onCompanySelect: (companyId: string) => {
                  onUpdateField("companyId", companyId);
                }
              } as TopFieldsContext
            }
          />
        }
        overviewContent={
          <div
            data-test="lead-detailed-view-overview"
            className="flex flex-col w-full gap-6"
          >
            {lead?.status === LeadStatus.CONVERTED && linkedDeal && (
              <LeadConvertedBanner linkedDealId={linkedDeal?.id} />
            )}
            {leadId && (
              <LeadDealSuggestionBanner
                leadId={leadId}
                leadStatus={lead?.status}
                onConvert={handleOpenConvertLeadModal}
              />
            )}
            {lead?.status === LeadStatus.CONFIRM_ICP && renderQualifyMessage()}
            <LeadKPIs lead={lead} />
            <div className="w-full flex gap-6">
              <div className="flex-1 min-w-0 flex flex-col gap-6">
                {isDemo && (
                  <FromDreamerForDemo entityType={EntityType.LEAD} />
                )}
                <LeadDetailFields
                  lead={lead}
                  onUpdateField={onUpdateField}
                  sources={sources}
                  licenseTypes={licenseTypes}
                />
              </div>
              <div className="flex-1 min-w-0 flex flex-col gap-6">
                <NextSteps
                  entity={lead}
                  entityType={EntityType.LEAD}
                  toggleTasksDrawer={toggleTasksDrawer}
                />
                <Ownership
                  entity={lead}
                  entityType={EntityType.LEAD}
                  onUpdateField={onUpdateField}
                  className="h-fit"
                />
              </div>
            </div>
          </div>
        }
      />
      {renderModals(isDropdownClosing, view, setView)}
    </>
  );
};

export default LeadDetailedView;
